import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  SIDEBAR_ATOM_ROW_HEIGHT,
  SIDEBAR_EXPANDABLE_LIST_PADDING_Y,
} from '../../modules/app-sidebar/views/sidebar-layout';

export const ROOT_APP_SIDEBAR_UI_STATE_STORAGE_PREFIX =
  'streamlain.rootAppSidebar.uiState.v1';

export const ROOT_APP_SIDEBAR_WORKSPACE_SECTION_DEFAULT_HEIGHT = 340;
export const ROOT_APP_SIDEBAR_SECTION_MIN_VISIBLE_ROWS = 4;
export const ROOT_APP_SIDEBAR_WORKSPACE_SECTION_MIN_HEIGHT = 0;
export const ROOT_APP_SIDEBAR_WORKSPACE_SECTION_MAX_HEIGHT = 520;
const ROOT_APP_SIDEBAR_PERSONAL_SECTION_DEFAULT_VISIBLE_ROWS = 7;
export const ROOT_APP_SIDEBAR_PERSONAL_SECTION_DEFAULT_HEIGHT =
  SIDEBAR_EXPANDABLE_LIST_PADDING_Y +
  SIDEBAR_ATOM_ROW_HEIGHT *
    ROOT_APP_SIDEBAR_PERSONAL_SECTION_DEFAULT_VISIBLE_ROWS;
export const ROOT_APP_SIDEBAR_PERSONAL_SECTION_MIN_HEIGHT =
  SIDEBAR_EXPANDABLE_LIST_PADDING_Y +
  SIDEBAR_ATOM_ROW_HEIGHT * ROOT_APP_SIDEBAR_SECTION_MIN_VISIBLE_ROWS;
export const ROOT_APP_SIDEBAR_PERSONAL_SECTION_MAX_HEIGHT = 520;

export type RootAppSidebarScrollArea =
  | 'workspace'
  | 'personal'
  | 'teamAreas'
  | 'apps'
  | 'others';

type RootAppSidebarScrollPositions = Partial<
  Record<RootAppSidebarScrollArea, number>
>;

export interface RootAppSidebarUiState {
  mobileActiveSection?: RootAppSidebarScrollArea | null;
  personalSectionHeight: number;
  workspaceSectionHeight: number;
  scrollTop: RootAppSidebarScrollPositions;
}

type StateUpdater<T> = T | ((current: T) => T);

const DEFAULT_ROOT_APP_SIDEBAR_UI_STATE: RootAppSidebarUiState = {
  mobileActiveSection: undefined,
  personalSectionHeight: ROOT_APP_SIDEBAR_PERSONAL_SECTION_DEFAULT_HEIGHT,
  workspaceSectionHeight: ROOT_APP_SIDEBAR_WORKSPACE_SECTION_DEFAULT_HEIGHT,
  scrollTop: {},
};

function storage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function finiteNonNegativeNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

export function clampRootAppSidebarWorkspaceSectionHeight(
  value: number
): number {
  return Math.min(
    ROOT_APP_SIDEBAR_WORKSPACE_SECTION_MAX_HEIGHT,
    Math.max(ROOT_APP_SIDEBAR_WORKSPACE_SECTION_MIN_HEIGHT, value)
  );
}

export function clampRootAppSidebarPersonalSectionHeight(
  value: number
): number {
  return Math.min(
    ROOT_APP_SIDEBAR_PERSONAL_SECTION_MAX_HEIGHT,
    Math.max(ROOT_APP_SIDEBAR_PERSONAL_SECTION_MIN_HEIGHT, value)
  );
}

export function snapRootAppSidebarPersonalSectionHeight(value: number): number {
  const clampedHeight = clampRootAppSidebarPersonalSectionHeight(value);

  const innerHeight = Math.max(
    0,
    clampedHeight - SIDEBAR_EXPANDABLE_LIST_PADDING_Y
  );
  const visibleRows = Math.max(
    ROOT_APP_SIDEBAR_SECTION_MIN_VISIBLE_ROWS,
    Math.round(innerHeight / SIDEBAR_ATOM_ROW_HEIGHT)
  );

  return clampRootAppSidebarPersonalSectionHeight(
    SIDEBAR_EXPANDABLE_LIST_PADDING_Y + visibleRows * SIDEBAR_ATOM_ROW_HEIGHT
  );
}

function normalizeScrollPositions(
  value: unknown
): RootAppSidebarScrollPositions {
  if (!isRecord(value)) return {};

  const scrollTop: RootAppSidebarScrollPositions = {};
  for (const key of [
    'workspace',
    'personal',
    'teamAreas',
    'apps',
    'others',
  ] as const) {
    const saved = finiteNonNegativeNumber(value[key]);
    if (saved !== null) {
      scrollTop[key] = saved;
    }
  }
  return scrollTop;
}

function normalizeMobileActiveSection(
  value: unknown
): RootAppSidebarScrollArea | null {
  return typeof value === 'string' &&
    ['workspace', 'personal', 'teamAreas', 'apps', 'others'].includes(value)
    ? (value as RootAppSidebarScrollArea)
    : null;
}

function normalizeRootAppSidebarUiState(
  value: unknown,
  options: { snapPersonalSectionHeight?: boolean } = {}
): RootAppSidebarUiState {
  if (!isRecord(value)) return DEFAULT_ROOT_APP_SIDEBAR_UI_STATE;

  const savedHeight = finiteNonNegativeNumber(value['workspaceSectionHeight']);
  const savedPersonalHeight = finiteNonNegativeNumber(
    value['personalSectionHeight']
  );
  const normalizedPersonalHeight =
    savedPersonalHeight === null
      ? ROOT_APP_SIDEBAR_PERSONAL_SECTION_DEFAULT_HEIGHT
      : clampRootAppSidebarPersonalSectionHeight(savedPersonalHeight);
  return {
    mobileActiveSection: Object.hasOwn(value, 'mobileActiveSection')
      ? normalizeMobileActiveSection(value['mobileActiveSection'])
      : undefined,
    personalSectionHeight:
      savedPersonalHeight === null
        ? ROOT_APP_SIDEBAR_PERSONAL_SECTION_DEFAULT_HEIGHT
        : options.snapPersonalSectionHeight
          ? snapRootAppSidebarPersonalSectionHeight(savedPersonalHeight)
          : normalizedPersonalHeight,
    workspaceSectionHeight:
      savedHeight === null
        ? ROOT_APP_SIDEBAR_WORKSPACE_SECTION_DEFAULT_HEIGHT
        : clampRootAppSidebarWorkspaceSectionHeight(savedHeight),
    scrollTop: normalizeScrollPositions(value['scrollTop']),
  };
}

function readRootAppSidebarUiState(key: string | null): RootAppSidebarUiState {
  const store = storage();
  if (!store || !key) return DEFAULT_ROOT_APP_SIDEBAR_UI_STATE;

  try {
    const raw = store.getItem(key);
    if (!raw) return DEFAULT_ROOT_APP_SIDEBAR_UI_STATE;
    return normalizeRootAppSidebarUiState(JSON.parse(raw) as unknown, {
      snapPersonalSectionHeight: true,
    });
  } catch {
    return DEFAULT_ROOT_APP_SIDEBAR_UI_STATE;
  }
}

function writeRootAppSidebarUiState(
  key: string | null,
  value: RootAppSidebarUiState
): void {
  const store = storage();
  if (!store || !key) return;

  try {
    store.setItem(key, JSON.stringify(value));
  } catch {
    // Local UI state must not break the sidebar in private/quota-limited modes.
  }
}

function encodeStorageScopePart(value: string): string {
  return encodeURIComponent(value.trim());
}

export function createRootAppSidebarUiStateStorageKey(
  accountId: string | null | undefined,
  workspaceId: string | null | undefined
): string | null {
  const normalizedAccountId = accountId?.trim();
  const normalizedWorkspaceId = workspaceId?.trim();
  if (!normalizedAccountId || !normalizedWorkspaceId) return null;

  return `${ROOT_APP_SIDEBAR_UI_STATE_STORAGE_PREFIX}:account:${encodeStorageScopePart(
    normalizedAccountId
  )}:workspace:${encodeStorageScopePart(normalizedWorkspaceId)}`;
}

function createRootAppSidebarUiStateFallbackStorageKey(
  workspaceId: string | null | undefined
): string | null {
  const normalizedWorkspaceId = workspaceId?.trim();
  if (!normalizedWorkspaceId) return null;
  return `${ROOT_APP_SIDEBAR_UI_STATE_STORAGE_PREFIX}:workspace:${encodeStorageScopePart(
    normalizedWorkspaceId
  )}`;
}

export function createRootAppSidebarResolvedUiStateStorageKey(
  accountId: string | null | undefined,
  workspaceId: string | null | undefined
): string | null {
  return (
    createRootAppSidebarUiStateStorageKey(accountId, workspaceId) ??
    createRootAppSidebarUiStateFallbackStorageKey(workspaceId)
  );
}

export function useRootAppSidebarUiState(
  accountId: string | null | undefined,
  workspaceId: string | null | undefined
) {
  const accountStorageKey = useMemo(
    () => createRootAppSidebarUiStateStorageKey(accountId, workspaceId),
    [accountId, workspaceId]
  );
  const fallbackStorageKey = useMemo(
    () => createRootAppSidebarUiStateFallbackStorageKey(workspaceId),
    [workspaceId]
  );
  const storageKey = accountStorageKey ?? fallbackStorageKey;
  const [state, setState] = useState<RootAppSidebarUiState>(() =>
    readRootAppSidebarUiState(storageKey)
  );
  const [restoreVersion, setRestoreVersion] = useState(0);

  useEffect(() => {
    setState(readRootAppSidebarUiState(storageKey));
    setRestoreVersion(version => version + 1);
  }, [storageKey]);

  const updateState = useCallback(
    (next: StateUpdater<RootAppSidebarUiState>) => {
      setState(current => {
        const value =
          typeof next === 'function'
            ? (
                next as (
                  current: RootAppSidebarUiState
                ) => RootAppSidebarUiState
              )(current)
            : next;
        const normalized = normalizeRootAppSidebarUiState(value);
        writeRootAppSidebarUiState(storageKey, normalized);
        if (fallbackStorageKey !== storageKey) {
          writeRootAppSidebarUiState(fallbackStorageKey, normalized);
        }
        return normalized;
      });
    },
    [fallbackStorageKey, storageKey]
  );

  const setWorkspaceSectionHeight = useCallback(
    (height: number) => {
      updateState(current => ({
        ...current,
        workspaceSectionHeight:
          clampRootAppSidebarWorkspaceSectionHeight(height),
      }));
    },
    [updateState]
  );

  const setPersonalSectionHeight = useCallback(
    (height: number) => {
      updateState(current => ({
        ...current,
        personalSectionHeight: clampRootAppSidebarPersonalSectionHeight(height),
      }));
    },
    [updateState]
  );

  const setScrollTop = useCallback(
    (area: RootAppSidebarScrollArea, scrollTop: number) => {
      const nextScrollTop = finiteNonNegativeNumber(scrollTop);
      if (nextScrollTop === null) return;
      updateState(current => ({
        ...current,
        scrollTop: {
          ...current.scrollTop,
          [area]: nextScrollTop,
        },
      }));
    },
    [updateState]
  );

  const setMobileActiveSection = useCallback(
    (section: RootAppSidebarScrollArea | null) => {
      updateState(current => ({
        ...current,
        mobileActiveSection: section,
      }));
    },
    [updateState]
  );

  const getScrollTop = useCallback(
    (area: RootAppSidebarScrollArea) => state.scrollTop[area] ?? 0,
    [state.scrollTop]
  );

  return {
    getScrollTop,
    setMobileActiveSection,
    setPersonalSectionHeight,
    setScrollTop,
    setWorkspaceSectionHeight,
    restoreVersion,
    state,
    storageKey,
  };
}
